import { FunctionComponent } from "react";
import { Link } from "react-router-dom";
import { TrustedPartner } from '../types';
import { useTrustedPartners } from '../hooks/useTrustedPartners';


const PartnerIcon: FunctionComponent<{ partner: TrustedPartner }> = ({ partner }) => (
  <div className="flex flex-col items-center text-center">
    <div className="w-32 h-32 border border-gray-200 rounded flex justify-center items-center p-2 mb-3">
      <img src={partner.logo} alt={`${partner.name} logo`} className="max-w-full max-h-full" />
    </div>
    <div className="w-full">
      <h3 className="text-sm font-medium text-primary mb-1">{partner.name}</h3>
      <p className="text-xs text-gray-600 mb-1">{partner.phone}</p>
      <p className="text-xs text-primary mb-1">{partner.email}</p>
      <a href={partner.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
        Link to Website
      </a>
    </div>
  </div>
);


const CategorySection: FunctionComponent<{ category: string; partners: TrustedPartner[] }> = ({
  category,
  partners,
}) => (
  <div className="mb-10">
    <div className="bg-blue-50 p-4 mb-5 rounded-t-md">
      <h2 className="text-xl font-semibold text-primary text-center">{category}</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
      {partners.map((partner) => (
        <PartnerIcon key={partner.id} partner={partner} />
      ))}
    </div>
  </div>
);


const BecomePartnerCTA: FunctionComponent = () => (
  <section className="bg-white py-12 px-8 text-center mb-10 border-2 border-primary rounded-md">
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-semibold text-primary mb-4">
        Interested in Becoming a Trusted Partner?
      </h2>
      <p className="text-gray-600 mb-8 text-lg">
        Join our network of trusted professionals and help clients achieve their financial goals.
        Expand your business opportunities while making a meaningful impact.
      </p>
      <Link to="/apply-partner">
        <button className="bg-primary text-white px-8 py-4 text-lg font-semibold rounded hover:bg-opacity-90 transition-colors">
          Apply Here
        </button>
      </Link>
    </div>
  </section>
);


const TrustedPartnersContent: FunctionComponent = () => {
  // ✅ USE THE HOOK!
  const { 
    partnersByCategory, 
    categories, 
    activeCategory, 
    setActiveCategory, 
    loading, 
    error 
  } = useTrustedPartners();


  if (loading) return <div className="text-center py-8">Loading partners...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;


  return (
    <div className="px-6 py-8">
      <section id="partners" className="mb-10">
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-blue-50 rounded-md p-1">
            <button
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                activeCategory === null ? "bg-primary text-white" : "text-primary hover:bg-gray-100"
              }`}
              onClick={() => setActiveCategory(null)}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  activeCategory === category
                    ? "bg-primary text-white"
                    : "text-primary hover:bg-gray-100"
                }`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        {activeCategory === null ? (
          categories.map((category) => (
            <CategorySection
              key={category}
              category={category}
              partners={partnersByCategory[category]}
            />
          ))
        ) : (
          <CategorySection
            category={activeCategory}
            partners={partnersByCategory[activeCategory]}
          />
        )}
      </section>


      <section className="bg-blue-50 py-12 px-8 text-center mb-10 rounded-md">
        <h2 className="text-2xl font-semibold text-primary mb-4">Your Trust, Our Priority</h2>
        <p className="text-gray-600 mb-6 max-w-3xl mx-auto">
          ClientVitals and our partners adhere to strict privacy and security standards. Your personal
          information is protected with advanced encryption and secure processes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <div className="w-10 h-10 mx-auto mb-3 text-primary">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
            <p className="text-xs text-gray-600">256-bit SSL Encryption</p>
          </div>
          <div>
            <div className="w-10 h-10 mx-auto mb-3 text-primary">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <p className="text-xs text-gray-600">Regulatory Compliance</p>
          </div>
          <div>
            <div className="w-10 h-10 mx-auto mb-3 text-primary">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
              </svg>
            </div>
            <p className="text-xs text-gray-600">Transparent Practices</p>
          </div>
        </div>
      </section>


      <BecomePartnerCTA />


      <section className="bg-gray-50 py-12 px-8 text-center rounded-md">
        <h2 className="text-2xl font-semibold text-primary mb-4">
          Questions About Our Partners?
        </h2>
        <p className="text-gray-600 mb-6">
          Contact us for more information about our trusted partner network.
        </p>
        <div className="space-y-2">
          <p className="text-primary font-medium">Phone: (802) 922-6268</p>
          <p className="text-primary font-medium">Email: info@clientvitals.com</p>
        </div>
      </section>
    </div>
  );
};


export default TrustedPartnersContent;


